// @ts-nocheck
// TypeScript is ignoring this file. If you are editing this file you must fix the type issues.

import {
  Menu, MenuDivider, MenuItem,
} from '@szhsin/react-menu';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ReactComponent as IconDuplicate } from '../../../../../../../assets/svg/shared/icon-duplicate-action.svg';
import { ReactComponent as IconDelete } from '../../../../../../../assets/icons/svg/shared/ic-shared-trash.svg';
import { ReactComponent as IconReminderOn } from '../../../../../../../assets/icons/svg/shared/ic-shared-reminder-on.svg';
import { ReactComponent as IconReminderOff } from '../../../../../../../assets/icons/svg/shared/ic-shared-reminder-off.svg';
import { threeDotsIcon } from '../../../../../../../assets/svg';
import { isBillStatusAwaitingDetails, isBillStatusCanceled, isBillStatusPending } from '../../../utils';
import {
  MenuClassName, MenuDividerClassName, MenuItemClassName, MenuItemIconClassName,
} from '../../../../../../../DesignSystem/ContextMenu/ContextMenuStyles';
import Heading from '../../../../../../../generic-components/Heading';
import Icon from '../../../../../../../generic-components/Icon';
import { useStore } from '../../../../../../../store';
import { BILL_ACTIONS_TEXTS } from '../../../consts/actions';
import { VIEWS_MAP } from '../../../../../../../router/constants';
import { BILLS_STATUSES } from '../../../../../consts';
import { trackMixPanel } from '../../../../../../../services/mixpanel.service';
import { AVAILABLE_BILL_MODALS } from '../../../components/BillsModalsHub/consts';
import { useSetBillReminder } from '../../../../../../../hooks/mutations/useSetBillReminder';
import LiliNotification, {
  LiliNotificationType,
} from '../../../../../../../DesignSystem/LiliNotification/LiliNotification';
import LiliToast, { LiliToastDefaultConfig } from '../../../../../../../DesignSystem/LiliToast/LiliToast';
import Spinner, { SpinnerColor } from '../../../../../../../generic-components/Spinner';

export const BillPreviewExtraActions = ({
  status, billId, paymentReminderOn, isReminderOptionEnabled,
} : { status: string, billId: string, paymentReminderOn: boolean, isReminderOptionEnabled: boolean }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedBill = useStore(state => state.selectedBill);
  const setSelectedBill = useStore(state => state.setSelectedBill);
  const setActiveBillModal = useStore(state => state.setActiveBillModal);
  const setShowBillDetailsModal = useStore(state => state.setShowBillDetailsModal);
  const updateNewItemByExternalId = useStore(state => state.updateNewItemByExternalId);
  const { mutate: requestBillReminderChange, isLoading: reminderChangeLoading } = useSetBillReminder((updatedBillItem) => {
    trackMixPanel('Act on specific bill preview', {
      Action: `Payment reminder - ${updatedBillItem.paymentReminderData?.paymentReminderEnabled ? 'on' : 'off'}`,
      'Bill type': `${updatedBillItem.calculatedStatus}`,
    });
    setSelectedBill(billId, (updatedBillItem));
    if (updatedBillItem?.calculatedStatus === BILLS_STATUSES.PENDING || updatedBillItem?.calculatedStatus === BILLS_STATUSES.AWAITING_DETAILS) {
      if (updatedBillItem) updateNewItemByExternalId(updatedBillItem);
    }
    toast(<LiliToast text={`Payment reminder ${updatedBillItem.paymentReminderData?.paymentReminderEnabled ? 'enabled' : 'disabled'}`} id='paymentReminderToggleSuccess' />, { ...LiliToastDefaultConfig });
  }, (errorData) => {
    toast(<LiliNotification type={LiliNotificationType.ERROR} text={errorData.response.data.errors[0].localizedMessage} id='paymentReminderToggleFailure' />);
  });

  const handleDuplicateAction = () => {
    if (selectedBill?.calculatedStatus === BILLS_STATUSES.PENDING) {
      trackMixPanel('Act on specific bill preview - pending', { Action: 'duplicate' });
    }
    if (selectedBill?.calculatedStatus === BILLS_STATUSES.AWAITING_DETAILS) {
      trackMixPanel('Act on specific bill preview - awaiting details', { Action: 'duplicate' });
    }
    if (selectedBill?.calculatedStatus === BILLS_STATUSES.PAID) {
      trackMixPanel('Act on specific bill preview - paid', { Action: 'duplicate' });
    }
    setShowBillDetailsModal(false);
    navigate(`/${VIEWS_MAP.BILLS.BASE}/${VIEWS_MAP.BILLS.CREATE_BILL.BASE}`, {
      state: {
        billId,
        billStatus: status,
      },
    });
  };

  const duplicateMenuItem = (
    <MenuItem className={MenuItemClassName} onClick={handleDuplicateAction}>
      <>
        <IconDuplicate className={MenuItemIconClassName} />
        <Heading title={`${t(BILL_ACTIONS_TEXTS.DUPLICATE)}`} className='font-metricR text-bw07 text-14px' />
      </>
    </MenuItem>
  );

  const handleReminderAction = () => {
    if (!isReminderOptionEnabled) return;
    const newPaymentReminderState = !paymentReminderOn;
    trackMixPanel('Act on specific bill preview - pending', {
      Action: `Payment reminder - ${newPaymentReminderState ? 'on' : 'off'}`,
    });
    requestBillReminderChange({ billId, paymentReminderEnabled: newPaymentReminderState });
  };

  const reminderMenuItem = (
    <MenuItem
      className={`flex flex-row min-w-[280px] gap-[8px] text-[14px] text-metricR text-bw08 hover:bg-bw02 hover:rounded-[4px] m-[4px] ${isReminderOptionEnabled ? '' : 'cursor-default opacity-50 hover:bg-bw01'}`}
      onClick={(e) => {
        // Stop the `onItemClick` of root menu component from firing
        e.stopPropagation = true;
        // Keep the menu open after this menu item is clicked
        e.keepOpen = true;
        handleReminderAction();
      }}
    >
      {reminderChangeLoading
        ? (
          <div className='flex justify-center w-full items-center py-[6px] cursor-pointer hover:bg-bw00'>
            <Spinner height='2rem' spinnerColor={SpinnerColor.Blue} width='2rem' />
          </div>
        ) : (
          <div className='flex items-center gap-[8px] py-[6px]'>
            {paymentReminderOn ? <IconReminderOn className={MenuItemIconClassName} />
              : <IconReminderOff className={MenuItemIconClassName} /> }
            <div className='flex flex-col'>
              <Heading title={`${paymentReminderOn ? t(BILL_ACTIONS_TEXTS.REMINDER_OFF) : t(BILL_ACTIONS_TEXTS.REMINDER_ON)}`} className='font-metricR text-bw07 text-14px' />
              <Heading title={`${t(BILL_ACTIONS_TEXTS.REMINDER_DESCRIPTION)}`} className='font-metricR text-xxs text-bw07 text-14px' />
            </div>
          </div>
        )}
    </MenuItem>
  );

  const handleDeleteAction = () => () => {
    if (selectedBill?.calculatedStatus === BILLS_STATUSES.PENDING) {
      trackMixPanel('Act on specific bill preview - pending', { Action: 'delete bill' });
    }
    if (selectedBill?.calculatedStatus === BILLS_STATUSES.AWAITING_DETAILS) {
      trackMixPanel('Act on specific bill preview - awaiting details', { Action: 'delete bill' });
    }
    if (selectedBill?.calculatedStatus === BILLS_STATUSES.PAID) {
      trackMixPanel('Act on specific bill preview - paid', { Action: 'delete bill' });
    }
    setActiveBillModal(AVAILABLE_BILL_MODALS.MARK_AS_CANCELED);
  };

  const deleteMenuItem = (
    <MenuItem className={MenuItemClassName} onClick={handleDeleteAction()}>
      <>
        <IconDelete className='MenuItemIconClassName text-red02' />
        <Heading title={`${t(BILL_ACTIONS_TEXTS.DELETE)}`} className='font-metricR text-red02 text-14px' />
      </>
    </MenuItem>
  );

  if (isBillStatusCanceled(status)) return null;
  return (
    <Menu
      menuButton={(
        <div data-testid='threeDots'>
          <Icon
            src={threeDotsIcon}
            className='flex justify-center rounded-full cursor-pointer hover:bg-bw02 hover:rounded-md'
          />
        </div>
      )}
      className={MenuClassName}
      transition
      direction='left'
      align='center'
      gap={8}
    >
      {(isBillStatusPending(status) || isBillStatusAwaitingDetails(status)) && (
      <>
        {duplicateMenuItem}
        <MenuDivider className={MenuDividerClassName} />
        {reminderMenuItem}
        <MenuDivider className={MenuDividerClassName} />
      </>
      )}
      {deleteMenuItem}
    </Menu>
  );
};
